// src/experiments/assignments.js
//
// The current assignments for this visitor. Recomputed when the config
// generation changes (remoteConfig.js bumps it when a remote document is
// applied) or when an input that assignment depends on changes, and served
// from a cache otherwise. Everything here is synchronous and fail-open.

import { assignAll, variantsOf } from './assign';
import { getContext } from './context';
import { getOverrides } from './overrides';
import { findExperiment, getExperiments, getRemoteForces } from './registry';
import { getSessionExposures } from './sessionExposures';
import { getUid } from './uid';

let generation = 0;
let cache = null;
const listeners = new Set();

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getGeneration() {
  return generation;
}

// `notify: false` is for a remote document applied during a route change:
// the components about to render read the new generation anyway, and nothing
// already on screen should switch arms mid-render.
export function invalidateAssignments(notify = true) {
  generation += 1;
  cache = null;
  if (notify) listeners.forEach((listener) => listener());
}

// Every enrolled experiment for this visitor on this page, e.g.
// { flow_v2: 'b' }. Crawlers and unenrolled visitors get {}.
export function getAssignments() {
  try {
    const ctx = getContext();
    const uid = getUid();
    const overrides = getOverrides();
    const remote = getRemoteForces();
    const sticky = getSessionExposures();
    const key = [
      generation,
      uid,
      ctx.pathname,
      ctx.product,
      ctx.device,
      ctx.isBot,
      JSON.stringify(overrides),
      JSON.stringify(sticky),
    ].join('|');
    if (cache && cache.key === key) return cache.value;

    const value = assignAll(getExperiments(), uid, ctx, overrides, {
      remote,
      sticky,
    });
    cache = { key, value };
    return value;
  } catch {
    return {};
  }
}

// The arm for one experiment, or null for control / not enrolled / unknown key.
export function getVariant(experimentKey) {
  try {
    const experiment = findExperiment(experimentKey);
    if (!experiment) return null;

    const assignments = getAssignments();
    if (Object.prototype.hasOwnProperty.call(assignments, experimentKey)) {
      return assignments[experimentKey];
    }
    // Crawlers are never enrolled; they explicitly get the first (control) arm.
    if (getContext().isBot) return variantsOf(experiment)[0]?.key ?? null;
    return null;
  } catch {
    return null;
  }
}

// True when a QA override is deciding this experiment's arm.
export function isForced(experimentKey) {
  try {
    return Object.prototype.hasOwnProperty.call(getOverrides(), experimentKey);
  } catch {
    return false;
  }
}
